import { memo, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import type { MouseEvent } from "react";

type SideMenuItemProps = {
	href?: string;
	/** 別タブ で開くかどうか */
	targetBlank?: boolean;

	/**
	 * 曖昧検索させる用のパス
	 */
	ambiguousPath?: string[];
	/**
	 * 除外するパス（単一または複数）
	 */
	excludePath?: string | string[];

	title: React.ReactNode;
	titleRightChildren?: React.ReactNode;

	icon?: React.ReactNode;
	handleClickButton?: (e: MouseEvent<HTMLButtonElement>) => void;

	callbackFunc?: () => void;
};

export const SideMenuItem = memo(
	({
		href,
		targetBlank = false, //
		ambiguousPath,
		excludePath,
		title,
		titleRightChildren,
		icon,
		handleClickButton,
		callbackFunc,
	}: SideMenuItemProps) => {
		const { pathname } = useLocation();

		// icon が存在するかどうか
		const hasIcon = useMemo(() => !!icon, [icon]);

		const isActive = useMemo(
			() =>
				(function () {
					// pathnameの最後に '/' が存在していた場合排除する
					const trimmedPathname = pathname.at(-1) === "/" ? pathname.slice(0, -1) : pathname;

					// 除外するパスの配列を作成
					const excludePaths = excludePath ? (Array.isArray(excludePath) ? excludePath : [excludePath]) : [];

					if (excludePaths.some((path) => trimmedPathname === path)) {
						// 除外するパスが設定されている & 除外するパスと現在のパスが完全一致する場合
						return false;
					} else if (ambiguousPath) {
						// 曖昧検索させる用のパスが設定されている場合
						return ambiguousPath.some((path) => trimmedPathname.startsWith(path));
					} else {
						return trimmedPathname === href;
					}
				})(),
			[ambiguousPath, excludePath, href, pathname],
		);

		const handleClick = useCallback(
			(e: MouseEvent<HTMLAnchorElement>) => {
				e.stopPropagation();
				callbackFunc && callbackFunc();
			},
			[callbackFunc],
		);

		return href ? (
			<Link
				target={targetBlank ? "_blank" : undefined}
				to={href}
				onClick={handleClick}
				className={cn("group flex items-center gap-3 rounded bg-white px-2.5 py-2 hover:bg-zinc-100")}
				viewTransition
			>
				{hasIcon && <div className={cn(isActive && "text-brand")}>{icon}</div>}

				<div
					className={cn(
						"flex w-full items-center justify-between text-[15px] leading-[24px]",
						isActive
							? "font-semibold text-zinc-900"
							: "text-zinc-500 transition-colors duration-300 ease-in-out group-hover:text-zinc-900",
					)}
				>
					<div>{title}</div>
					{titleRightChildren}
				</div>
			</Link>
		) : (
			<button
				onClick={handleClickButton}
				className={cn("flex w-full items-center gap-3 rounded bg-white px-2.5 py-2 hover:bg-zinc-100")}
			>
				{hasIcon && <div className={cn(isActive && "text-brand")}>{icon}</div>}
				<p className={cn("text-[15px] leading-[24px] text-zinc-900", isActive && "font-semibold")}>{title}</p>
			</button>
		);
	},
);

SideMenuItem.displayName = "SideMenuItem";
